/**
 * Phase 10b — Section "Coûts fournisseurs" sur la fiche chantier.
 *
 * Affiche la liste des factures fournisseurs rattachées au chantier + les totaux
 * HTVA / TVA / TVAC. Accessible admin + secrétaire uniquement — le rôle commercial
 * ne doit voir NI les coûts détaillés (endpoint 403) NI la marge sur achats
 * (cf. ChantierMarginBlock). Composant invisible sur 403.
 *
 * Format monétaire : fr-BE, `1 234,56 €`.
 * Formats date : court fr-BE en backoffice (`05/09/2026`).
 */
import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileText, Receipt } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const money = (n) =>
  new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const shortDateBE = (iso) => {
  if (!iso) return "—";
  // Le backend renvoie soit "YYYY-MM-DD" (issue_date) soit ISO datetime
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return "—";
  return `${d}/${m}/${y}`;
};

const STATUS_LABELS = {
  received: { label: "Reçue", cls: "badge-neutral" },
  validated: { label: "Validée", cls: "badge-info" },
  paid: { label: "Payée", cls: "badge-success" },
  rejected: { label: "Rejetée", cls: "badge-error" },
};

export default function ChantierSupplierCostsSection({ chantierId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const { data } = await api.get(`/chantiers/${chantierId}/supplier-costs`);
      setData(data);
    } catch (e) {
      // 403 = commercial (endpoint désormais require_admin_or_secretary) → composant invisible
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  }, [chantierId]);

  useEffect(() => { load(); }, [load]);

  if (forbidden) return null;

  if (loading) {
    return (
      <div className="rounded border border-slate-800 bg-slate-900/40 p-6" data-testid="supplier-costs-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
      </div>
    );
  }

  const invoices = data?.invoices || [];

  return (
    <div className="space-y-3" data-testid="chantier-supplier-costs-section">
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <Receipt className="h-4 w-4 text-[#C4553B]" />
        <span className="font-medium text-slate-200">
          {data?.count ?? 0} facture{(data?.count ?? 0) > 1 ? "s" : ""} fournisseur{(data?.count ?? 0) > 1 ? "s" : ""}
        </span>
      </div>

      {invoices.length === 0 ? (
        <div
          className="rounded border border-dashed border-slate-800 bg-slate-950/30 p-6 text-center text-sm text-slate-500"
          data-testid="supplier-costs-empty"
        >
          <FileText className="mx-auto mb-2 h-6 w-6 text-slate-600" />
          Aucune facture fournisseur rattachée à ce chantier.
          <div className="mt-1 text-xs">
            Les factures reçues via Peppol peuvent être rattachées depuis la boîte de réception fournisseurs.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-slate-800 bg-slate-900/40">
          <table className="w-full text-sm" data-testid="supplier-costs-table">
            <thead className="border-b border-slate-800 text-xs uppercase text-slate-500">
              <tr>
                <th className="p-3 text-left">Fournisseur</th>
                <th className="p-3 text-left">N° facture</th>
                <th className="p-3 text-left">Émission</th>
                <th className="p-3 text-right">HTVA</th>
                <th className="p-3 text-right">TVA</th>
                <th className="p-3 text-right">TVAC</th>
                <th className="p-3 text-left">Statut</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const st = STATUS_LABELS[inv.status] || STATUS_LABELS.received;
                return (
                  <tr
                    key={inv.id}
                    className="border-b border-slate-800/60 last:border-0"
                    data-testid={`supplier-costs-row-${inv.id}`}
                  >
                    <td className="p-3 font-medium">{inv.supplier_name || "—"}</td>
                    <td className="p-3 font-mono text-xs text-slate-300">{inv.invoice_number || "—"}</td>
                    <td className="p-3 text-slate-400">{shortDateBE(inv.issue_date)}</td>
                    <td className="p-3 text-right tabular-nums">{money(inv.amount_htva)}</td>
                    <td className="p-3 text-right tabular-nums text-slate-400">{money(inv.amount_vat)}</td>
                    <td className="p-3 text-right tabular-nums font-medium">{money(inv.amount_tvac)}</td>
                    <td className="p-3">
                      <Badge className={`border text-xs font-normal ${st.cls}`}>{st.label}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-slate-700 bg-slate-950/40">
              <tr data-testid="supplier-costs-totals">
                <td className="p-3 font-semibold text-slate-200" colSpan={3}>
                  Total factures fournisseurs
                </td>
                <td className="p-3 text-right tabular-nums font-semibold text-slate-100" data-testid="supplier-costs-total-htva">
                  {money(data?.total_htva || 0)}
                </td>
                <td className="p-3 text-right tabular-nums text-slate-400">{money(data?.total_vat || 0)}</td>
                <td className="p-3 text-right tabular-nums font-semibold text-slate-100" data-testid="supplier-costs-total-tvac">
                  {money(data?.total_tvac || 0)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
